import Icon from "@chakra-ui/icon";
import { Box, Flex } from "@chakra-ui/layout";
import * as React from "react";
import { components, MenuListComponentProps } from "react-select";
import AsyncSelect from "react-select/async";
import { useDebouncedCallback } from "use-debounce";
import { SelectLoadingIndicator } from "./SelectLoadingIndicator";
import { MdLocationSearching } from "react-icons/md";
import { useColorMode, useColorModeValue } from "@chakra-ui/react";

const searchIconPrefix = {
  display: "flex",
  alignItems: "center",
  ":before": {
    background: "var(--chakra-colors-gray-400)",
    maskImage:
      "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1024 1024'><path  d='M909.6 854.5L649.9 594.8C690.2 542.7 712 479 712 412c0-80.2-31.3-155.4-87.9-212.1-56.6-56.7-132-87.9-212.1-87.9s-155.5 31.3-212.1 87.9C143.2 256.5 112 331.8 112 412c0 80.1 31.3 155.5 87.9 212.1C256.5 680.8 331.8 712 412 712c67 0 130.6-21.8 182.7-62l259.7 259.6a8.2 8.2 0 0 0 11.6 0l43.6-43.5a8.2 8.2 0 0 0 0-11.6zM570.4 570.4C528 612.7 471.8 636 412 636s-116-23.3-158.4-65.6C211.3 528 188 471.8 188 412s23.3-116.1 65.6-158.4C296 211.3 352.2 188 412 188s116.1 23.2 158.4 65.6S636 352.2 636 412s-23.3 116.1-65.6 158.4z'/></svg>\")",
    maskRepeat: "no-repeat",
    display: "block",
    content: '" "',
    marginRight: 16,
    height: 16,
    minWidth: 16,
  },
};

const selectStyles = {
  control: (styles: any, { selectProps: { colorMode } }: any) => ({
    ...styles,
    background: colorMode === "light" ? "hsl(0, 0%, 100%)" : "inherit",
    borderColor: colorMode === "light" ? "hsl(0, 0%, 80%)" : "inherit",
  }),
  menu: (styles: any, { selectProps: { colorMode } }: any) => ({
    ...styles,
    boxShadow:
      colorMode === "light"
        ? "0 0 0 1px hsla(0, 0%, 0%, 0.1),0 4px 11px hsla(0, 0%, 0%, 0.1)"
        : "0 0 0 1px hsl(0deg 0% 100% / 10%), 0 4px 11px hsl(0deg 0% 100% / 10%)",
  }),
  input: (styles: any, { selectProps: { colorMode } }: any) => ({
    ...styles,
    ...searchIconPrefix,
    color:
      colorMode === "light"
        ? "hsl(0, 0%, 20%)"
        : "var(--chakra-colors-whiteAlpha-900)",
  }),
  placeholder: (styles: any) => ({
    ...styles,
    ...searchIconPrefix,
  }),
  singleValue: (styles: any, { selectProps: { colorMode } }: any) => ({
    ...styles,
    ...searchIconPrefix,
    color:
      colorMode === "light"
        ? "hsl(0, 0%, 20%)"
        : "var(--chakra-colors-whiteAlpha-900)",
  }),
};

interface PlaceOption {
  label: string;
  value: google.maps.places.AutocompletePrediction;
}

interface Props {
  location?: google.maps.LatLngLiteral;
  radius?: number;
  minLengthAutocomplete?: number;
  value: PlaceOption | null;
  onChange: (value: PlaceOption | null) => void;
  onFindOnMap?: (geoPoint: google.maps.LatLngLiteral) => void;
}

export function PlacesAutocomplete({
  minLengthAutocomplete = 0,
  value,
  location,
  radius,
  onChange,
  onFindOnMap,
}: Props) {
  const { colorMode } = useColorMode();
  const [placesService, setPlacesService] = React.useState<
    google.maps.places.AutocompleteService | undefined
  >();
  React.useEffect(() => {
    setPlacesService(new window.google.maps.places.AutocompleteService());
  }, []);

  const handleLoadOptionsCallback = useDebouncedCallback(function loadOptions(
    value: string,
    callback: (options: PlaceOption[]) => void
  ) {
    if (!placesService || value.length < minLengthAutocomplete) {
      callback([]);
      return;
    }
    placesService.getPlacePredictions(
      {
        input: value,
        location: location ? new google.maps.LatLng(location) : undefined,
        radius,
      },
      (predictions, status) => {
        if (
          predictions == null ||
          status !== google.maps.places.PlacesServiceStatus.OK
        ) {
          callback([]);
          return;
        }
        callback(
          predictions.map((prediction) => {
            return {
              label: prediction.description,
              value: prediction,
            };
          })
        );
      }
    );
  },
  300);
  return (
    <AsyncSelect
      cacheOptions
      placeholder="Enter your street"
      styles={selectStyles}
      value={value}
      onChange={onChange}
      loadOptions={handleLoadOptionsCallback}
      components={{
        LoadingIndicator: SelectLoadingIndicator,
        DropdownIndicator: () => null,
        IndicatorSeparator: () => null,
        ...(!!onFindOnMap && {
          MenuList: WithFindOnMapMenuList,
        }),
      }}
      noOptionsMessage={() => null}
      getOptionLabel={({ label }) => label}
      getOptionValue={({ value }) => value.place_id}
      //@ts-ignore
      onFindOnMap={onFindOnMap}
      ref={(selectInput) => {
        if (selectInput) {
          selectInput.focus();
        }
      }}
      colorMode={colorMode}
      theme={
        colorMode === "dark"
          ? (theme) => ({
              ...theme,
              colors: {
                ...theme.colors,
                primary25: "#2D3748",
                neutral0: "#1a202c",
              },
            })
          : undefined
      }
    />
  );
}

const WithFindOnMapMenuList = (
  props: MenuListComponentProps<PlaceOption, false>
) => {
  const bgColor = useColorModeValue("white", "1a202c");
  return (
    <components.MenuList {...props}>
      <Box>
        {props.children}
        <Flex
          bgColor={bgColor}
          borderTop="1px"
          borderColor="gray.200"
          py="2"
          px="3"
          cursor="pointer"
          align="center"
          //@ts-ignore
          onClick={props.selectProps.onFindOnMap}>
          <Icon as={MdLocationSearching} mr={4} color="gray.400" />
          Find on map
        </Flex>
      </Box>
    </components.MenuList>
  );
};
